import React, { useState, useEffect } from "react";
import { gcpTextToSpeech } from "../../../../../api/gcp";
import Recording from "./Recording";
import { ImprovedResponseData } from "../types";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";

interface ImprovedCandidateResponseProps {
  answers: ImprovedResponseData | null;
}

interface CachedAudioData {
  storageUrl: string;
  timestamp: number;
}

const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_COLLECTION = "audioCache";

const ImprovedCandidateResponse: React.FC<ImprovedCandidateResponseProps> = ({
  answers,
}) => {
  const [audioUrls, setAudioUrls] = useState<{ [key: string]: string }>({});
  const [loadingAudio, setLoadingAudio] = useState<{
    [key: string]: boolean;
  }>({});
  const [audioErrors, setAudioErrors] = useState<{ [key: string]: string }>({});

  const storage = getStorage();
  const firestore = getFirestore();

  const getCacheKey = (id: string) => `audio_cache_${id}`;

  const getFromCache = async (id: string): Promise<string | null> => {
    try {
      const cacheRef = doc(firestore, CACHE_COLLECTION, getCacheKey(id));
      const cacheDoc = await getDoc(cacheRef);

      if (!cacheDoc.exists()) {
        return null;
      }

      const data = cacheDoc.data() as CachedAudioData;

      // Check if cache has expired
      if (Date.now() - data.timestamp > CACHE_EXPIRY_TIME) {
        // Delete expired cache
        await Promise.all([
          deleteDoc(cacheRef),
          deleteObject(ref(storage, data.storageUrl)),
        ]);
        return null;
      }

      return data.storageUrl;
    } catch (error) {
      console.error("Error reading from cache:", error);
      return null;
    }
  };

  const clearOldCache = async () => {
    try {
      const expiredTimestamp = Date.now() - CACHE_EXPIRY_TIME;
      const q = query(
        collection(firestore, CACHE_COLLECTION),
        where("timestamp", "<", expiredTimestamp),
        limit(50)
      );

      const snapshot = await getDocs(q);

      const deletePromises = snapshot.docs.map(async (doc) => {
        const data = doc.data() as CachedAudioData;
        await Promise.all([
          deleteDoc(doc.ref),
          deleteObject(ref(storage, data.storageUrl)),
        ]);
      });

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error clearing old cache:", error);
    }
  };

  const saveToCache = async (id: string, audioBlob: Blob): Promise<string> => {
    try {
      // Generate a unique storage path
      const storagePath = `audio-cache/${id}-${Date.now()}.mp3`;
      const storageRef = ref(storage, storagePath);

      // Upload the audio blob
      await uploadBytes(storageRef, audioBlob);
      const url = await getDownloadURL(storageRef);

      // Save metadata to Firestore
      const cacheRef = doc(firestore, CACHE_COLLECTION, getCacheKey(id));
      await setDoc(cacheRef, {
        storageUrl: url,
        timestamp: Date.now(),
        path: storagePath,
      });

      // Trigger cleanup of old cache entries
      clearOldCache();

      return url;
    } catch (error) {
      console.error("Error saving to cache:", error);
      throw error;
    }
  };

  const base64ToBlob = async (base64: string): Promise<Blob> => {
    const response = await fetch(base64);
    return response.blob();
  };

  useEffect(() => {
    const generateAudio = async (text: string, id: string) => {
      if (!text || !id) return;

      setLoadingAudio((prev) => ({ ...prev, [id]: true }));
      setAudioErrors((prev) => ({ ...prev, [id]: "" }));

      try {
        // Check cache first
        const cachedUrl = await getFromCache(id);

        if (cachedUrl) {
          setAudioUrls((prev) => ({ ...prev, [id]: cachedUrl }));
        } else {
          // Generate new audio
          const apiKey = process.env.REACT_APP_GCP_TTS_API_KEY;
          if (!apiKey) {
            throw new Error("Google Cloud API key is not configured");
          }

          const audioBlob = await gcpTextToSpeech(text, apiKey);
          if (!audioBlob) {
            throw new Error("Failed to generate audio blob");
          }

          // Save to cache and get URL
          try {
            const url = await saveToCache(id, audioBlob);
            setAudioUrls((prev) => ({ ...prev, [id]: url }));
          } catch (cacheError) {
            console.warn("Failed to cache audio:", cacheError);
            // Create a temporary URL for playback even if caching failed
            const tempUrl = URL.createObjectURL(audioBlob);
            setAudioUrls((prev) => ({ ...prev, [id]: tempUrl }));
          }
        }
      } catch (error) {
        console.error("Error generating audio:", error);
        setAudioErrors((prev) => ({
          ...prev,
          [id]:
            error instanceof Error ? error.message : "Failed to generate audio",
        }));
      } finally {
        setLoadingAudio((prev) => ({ ...prev, [id]: false }));
      }
    };

    // Cleanup previous audio URLs
    Object.values(audioUrls).forEach((url) => URL.revokeObjectURL(url));
    setAudioUrls({});

    // Generate new audio if we have an answer
    if (answers?.improvedResponse?.improved_response?.paragraph_version) {
      generateAudio(
        answers.improvedResponse.improved_response.paragraph_version,
        answers.id
      );
    }

    return () => {
      // Only revoke object URLs that we created temporarily (not Firebase Storage URLs)
      Object.entries(audioUrls).forEach(([id, url]) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [answers]);

  if (
    !answers ||
    answers.questionType === "quantitative" ||
    answers.questionType === "math"
  ) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-[#E6E8EB] transition-all ">
        <div className="bg-gradient-to-r from-[#F5F6F7] to-[#F8F9FA] p-6 rounded-t-xl border-b border-[#E6E8EB]">
          <h3 className="text-lg font-semibold text-[#1F2024] tracking-tight">
            Improved Candidate Response
          </h3>
        </div>
        <div className="p-6 text-sm flex flex-col items-center justify-center min-h-[200px] space-y-4">
          <p className="text-[#474A52]">
            {!answers
              ? "No improved responses available."
              : "This is a quantitative/mathematical question type."}
          </p>
          <p className="text-[#6366F1] font-medium">
            Please check the Detailed Case Component Analysis section for
            detailed answer breakdown and calculations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-[#E6E8EB]">
      <div className="bg-gradient-to-r from-[#F5F6F7] to-[#F8F9FA] p-4 rounded-t-xl border-b border-[#E6E8EB]">
        <h3 className="text-lg font-semibold text-text-primary tracking-tight">
          Improved Candidate Response
        </h3>
      </div>

      <div className="p-6 max-h-[500px] overflow-y-auto">
        <div className="mb-8">
          {/* Audio Section */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              Audio Example
            </h4>
            <div className="mb-4 bg-[#FAFBFC] rounded-lg p-4">
              {loadingAudio[answers.id] ? (
                <div className="text-[14px] text-text-secondary flex items-center">
                  <svg
                    className="animate-spin h-4 w-4 mr-2 text-[#6366F1]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Generating audio...
                </div>
              ) : audioErrors[answers.id] ? (
                <div className="text-[14px] text-red-500 flex items-center">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {audioErrors[answers.id]}
                </div>
              ) : (
                <Recording
                  url={audioUrls[answers.id]}
                  questionId={answers.id}
                  key={`recording-${answers.id}-${audioUrls[answers.id]}`}
                />
              )}
            </div>
          </div>

          {/* Bullet Points Version */}
          {answers.improvedResponse.improved_response.bullet_point_version.map(
            (section, sectionIndex: number) => (
              <div key={sectionIndex} className="mb-8 last:mb-0">
                <h4 className="text-lg font-semibold text-text-primary mb-4">
                  {section.title}
                </h4>
                <div className="space-y-4">
                  {section.explanation.map((point, pointIndex: number) => (
                    <div key={pointIndex} className="ml-4">
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-[8px] mr-3 flex-shrink-0"></div>
                        <div className="flex-1">
                          <span className="text-[14px] font-bold text-text-primary">
                            {point.subtitle}:
                          </span>
                          <span className="text-[14px] text-text-secondary ml-2">
                            {point.content}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ImprovedCandidateResponse;
